import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers, FormikProps, useFormikContext } from "formik";
import { useDispatch } from "react-redux";
import { IVacationBidSlot, IVacationBidSlotGeneration, IVacationDate } from "@vmosolutions/oc-interfaces";

// Components
import { FormikDateTimeField, FormikTextField } from "src/components/FormikComponents/FormikWrappers";
import { INotifications } from "src/API/AmplifyAPIWrapper";

import { defaultSlotGeneration, generateVacationBidSlots, isoToVacationDate, vacationDateToIso } from "../helpers";
import { SlotGenerationSchema } from "../vacationBidPackageSchema";
import { VACATION_BID_TEST_ID } from "../testIds";

export interface IGenerateSlotsDialogProps {
  open: boolean;
  onClose: () => void;
  packageId: string | undefined;
  bidBlockStart: Date | string | undefined;
  /** Last-used parameters, laid over the defaults to prefill the form. */
  slotGeneration: IVacationBidSlotGeneration | undefined;
  /**
   * Receives the candidate slots and the parameters that produced them. Nothing is saved until the
   * slot table itself is saved.
   */
  onGenerated: (slots: IVacationBidSlot[], generation: IVacationBidSlotGeneration) => void;
}

/** The dialog's form model. Dates are `YYYY-MM-DD` strings, the form the date picker works in. */
interface ISlotGenerationForm {
  firstSlotStart: string;
  slotLengthDays: number;
  repeatEveryDays: number;
  defaultCapacity: number;
  blackoutDates: string[];
}

type SlotGenerationFormProps = FormikProps<ISlotGenerationForm>;

const GenerateSlotsDialogStyles = (theme: Theme): any => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    minWidth: 540,
    "& .MuiDialogTitle-root": {
      ...theme.typography.subtitle2,
      fontSize: 16,
      padding: "12px 16px",
    },
    "& .MuiDialogContent-root": {
      padding: "8px 16px",
      "& .section_label": {
        margin: "8px 5px 0px",
        fontWeight: 300,
        fontSize: 10,
        textTransform: "uppercase",
        color: theme.custom.forms.labels,
      },
      "& .field": {
        width: "auto",
        minWidth: "238px",
        "&.datetimefield label": { width: "130px" },
        "&.textfield .input_control": {
          "& label": { width: "130px" },
          "& .input-base": { width: "80px" },
        },
      },
      "& .preset_buttons": {
        margin: "0px 5px",
        "& .MuiButtonBase-root": { padding: "2px 8px", margin: "0px 5px 0px 0px", fontSize: 12 },
      },
      "& .blackout_dates": {
        margin: "0px 5px",
        "& .blackout_row": { display: "flex", alignItems: "center" },
        "& .blackout_empty": { margin: "5px", fontSize: 12, color: theme.custom.forms.labels },
        "& .add_blackout": { padding: "2px 8px", margin: "5px 0px", fontSize: 12 },
      },
    },
    "& .MuiDialogActions-root .MuiButtonBase-root": { padding: "2px 8px", margin: "0px 5px", fontSize: 12 },
  },
});

/** Defaults for the bid block, overlaid with the package's last-used parameters, as form strings. */
const toFormValues = (
  bidBlockStart: Date | string | undefined,
  slotGeneration: IVacationBidSlotGeneration | undefined
): ISlotGenerationForm => {
  const generation: IVacationBidSlotGeneration = { ...defaultSlotGeneration(bidBlockStart), ...slotGeneration };
  return {
    firstSlotStart: vacationDateToIso(generation.firstSlotStart),
    slotLengthDays: generation.slotLengthDays,
    repeatEveryDays: generation.repeatEveryDays,
    defaultCapacity: generation.defaultCapacity,
    blackoutDates: (generation.blackoutDates ?? []).map(vacationDateToIso),
  };
};

/** Form strings back to the request shape. Blank blackout rows are dropped. */
const toGeneration = (values: ISlotGenerationForm): IVacationBidSlotGeneration => ({
  firstSlotStart: isoToVacationDate(values.firstSlotStart),
  slotLengthDays: values.slotLengthDays,
  repeatEveryDays: values.repeatEveryDays,
  defaultCapacity: values.defaultCapacity,
  blackoutDates: values.blackoutDates.map(isoToVacationDate).filter((date): date is IVacationDate => !!date),
});

const newBlackoutKey = (): string => crypto.randomUUID();

/**
 * The blackout date list. Each row carries its own key so a picker keeps its state when a row
 * above it is removed.
 */
const BlackoutDatesField = (): JSX.Element => {
  const { values } = useFormikContext<ISlotGenerationForm>();
  const dates = values.blackoutDates;

  const [keys, setKeys] = useState<string[]>(() => dates.map(newBlackoutKey));
  // Keys follow the list when its length changes other than through the buttons below.
  const rowKeys = keys.length === dates.length ? keys : dates.map((date, index) => keys[index] ?? newBlackoutKey());
  if (rowKeys !== keys) setKeys(rowKeys);

  return (
    <FieldArray name="blackoutDates">
      {(arrayHelpers: FieldArrayRenderProps) => (
        <div className="blackout_dates" data-testid={VACATION_BID_TEST_ID.generateBlackoutDates}>
          {dates.length === 0 && (
            <Typography className="blackout_empty">
              No blackout dates - every generated block starts included.
            </Typography>
          )}
          {dates.map((blackoutDate: string, index: number) => (
            <div className="blackout_row" key={rowKeys[index]}>
              <FormikDateTimeField
                className="field datetimefield"
                label={`Blackout ${index + 1}`}
                name={`blackoutDates.${index}`}
                hideTimeControl={true}
              />
              <Tooltip title="Remove blackout date" placement="top">
                <IconButton
                  size="small"
                  aria-label="Remove blackout date"
                  onClick={() => {
                    setKeys(rowKeys.filter((key, keyIndex) => keyIndex !== index));
                    arrayHelpers.remove(index);
                  }}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </div>
          ))}
          <Button
            className="add_blackout"
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            aria-label="Add blackout date"
            onClick={() => {
              setKeys([...rowKeys, newBlackoutKey()]);
              arrayHelpers.push("");
            }}
          >
            Add Blackout Date
          </Button>
        </div>
      )}
    </FieldArray>
  );
};

/**
 * Generate Vacation Blocks dialog - asks the backend for candidate slots from a start date, slot
 * length, repeat interval, capacity and blackout dates, and hands them to the slot table for review.
 */
export const GenerateSlotsDialog = (props: IGenerateSlotsDialogProps): JSX.Element => {
  const { open, onClose, packageId, bidBlockStart, slotGeneration, onGenerated } = props;

  const dispatch = useDispatch();

  const initialValues = useMemo<ISlotGenerationForm>(
    () => toFormValues(bidBlockStart, slotGeneration),
    [bidBlockStart, slotGeneration]
  );

  const handleGenerate = useCallback(
    async (values: ISlotGenerationForm, helpers: FormikHelpers<ISlotGenerationForm>): Promise<void> => {
      const { setSubmitting } = helpers;
      if (!packageId) {
        setSubmitting(false);
        return;
      }
      const notifications: INotifications = { dispatch, errorMessage: "Unable to generate vacation blocks" };
      const generation = toGeneration(values);
      const slots: IVacationBidSlot[] | null = await generateVacationBidSlots(packageId, generation, notifications);
      if (slots) {
        onGenerated(slots, generation);
        onClose();
      }
      setSubmitting(false);
    },
    [dispatch, packageId, onGenerated, onClose]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid={VACATION_BID_TEST_ID.generateDialog}
      PaperProps={{ sx: (theme: Theme) => GenerateSlotsDialogStyles(theme).paper }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={SlotGenerationSchema}
        onSubmit={(values: ISlotGenerationForm, helpers: FormikHelpers<ISlotGenerationForm>) => {
          void handleGenerate(values, helpers);
        }}
      >
        {(generationForm: SlotGenerationFormProps) => {
          const { values, isValid, isSubmitting, setFieldValue } = generationForm;
          const slotLengthIsUsable = Number.isFinite(values.slotLengthDays) && values.slotLengthDays >= 1;

          return (
            <Form>
              <DialogTitle>Generate Vacation Blocks</DialogTitle>
              <DialogContent>
                <Grid container direction="column" alignItems="flex-start">
                  <Typography className="section_label">schedule</Typography>
                  <FormikDateTimeField
                    className="field datetimefield"
                    label="First Slot Start"
                    name="firstSlotStart"
                    hideTimeControl={true}
                    data-testid={VACATION_BID_TEST_ID.generateFirstSlotStart}
                  />
                  <FormikTextField
                    className="field textfield"
                    label="Slot Length (days)"
                    name="slotLengthDays"
                    type="number"
                    inputProps={{ "data-testid": VACATION_BID_TEST_ID.generateSlotLengthDays, min: 1 }}
                  />
                  <FormikTextField
                    className="field textfield"
                    label="Repeat Every (days)"
                    name="repeatEveryDays"
                    type="number"
                    inputProps={{ "data-testid": VACATION_BID_TEST_ID.generateRepeatEveryDays, min: 1 }}
                  />
                  <div className="preset_buttons">
                    <Tooltip title="Every slot starts on the same weekday" placement="top">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => void setFieldValue("repeatEveryDays", 7)}
                        data-testid={VACATION_BID_TEST_ID.generateWeeklyPreset}
                      >
                        Same weekday each week
                      </Button>
                    </Tooltip>
                    <Tooltip title="Each slot starts the day after the previous one ends" placement="top">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!slotLengthIsUsable}
                          onClick={() => void setFieldValue("repeatEveryDays", values.slotLengthDays)}
                          data-testid={VACATION_BID_TEST_ID.generateBackToBackPreset}
                        >
                          Back-to-back
                        </Button>
                      </span>
                    </Tooltip>
                  </div>

                  <Typography className="section_label">capacity</Typography>
                  <FormikTextField
                    className="field textfield"
                    label="Default Capacity"
                    name="defaultCapacity"
                    type="number"
                    inputProps={{ "data-testid": VACATION_BID_TEST_ID.generateDefaultCapacity, min: 0 }}
                  />

                  <Typography className="section_label">blackout dates</Typography>
                  <BlackoutDatesField />
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isSubmitting}
                  onClick={onClose}
                  data-testid={VACATION_BID_TEST_ID.generateCancelButton}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !isValid || !packageId}
                  data-testid={VACATION_BID_TEST_ID.generateConfirmButton}
                >
                  Generate
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default GenerateSlotsDialog;
